import { mapAccountSectionToAuditTag } from '../../../../../backend/features/accounts/shared';
import { useContactManagementTranslation } from '../../../../../ui/hooks';
import { ContactManagementConfigContext } from '../../../../../ui/providers';
import { Form } from '@juvare/common-form-ui/Form';
import { FormConfigContext } from '@juvare/common-form-ui/FormConfigContext';
import { NavigationContext } from '@juvare/common-web-ui/NavigationContext';
import { regionEntityName } from '@juvare/platform-sdk-react/backend/features/default-classifiers/shared';
import { usePublicDataGet } from '@juvare/platform-sdk-react/ui/hooks/usePublicDataGet';
import { Alert } from '@mui/material';
import React from 'react';
import { AccountGroupUser } from './components/AccountGroupUser';
import { AccountMembershipAnonymous } from './components/AccountMembershipAnonymous';
import { AccountMembershipUser } from './components/AccountMembershipUser';
export const AccountSectionComponent = React.forwardRef((props, ref) => {
    var _a, _b, _c;
    const { t } = useContactManagementTranslation();
    const context = React.useContext(ContactManagementConfigContext);
    const navigationContext = React.useContext(NavigationContext);
    const { userState } = React.useContext(context.appContext);
    const regions = usePublicDataGet(`classifier-${regionEntityName}`, context.appName);
    const section = usePublicDataGet(`accountSection-${props.section.id}`, context.appName);
    const sectionResponse = section.data;
    const sectionError = section.error || regions.error;
    const sectionComponentRef = React.useRef(null);
    const formParams = {
        id: props.accountId,
        page: props.page,
        userState,
        regionsData: (_a = regions.data) !== null && _a !== void 0 ? _a : [],
    };
    React.useImperativeHandle(ref, () => ({
        getData: () => {
            var _a, _b;
            return (_b = (_a = sectionComponentRef.current) === null || _a === void 0 ? void 0 : _a.getData()) !== null && _b !== void 0 ? _b : {};
        },
        submit: () => {
            if (sectionComponentRef.current) {
                return sectionComponentRef.current.submit();
            }
            return Promise.reject('Something went wrong.');
        },
    }));
    if (sectionResponse === null || sectionResponse === void 0 ? void 0 : sectionResponse.data) {
        const auditTags = [mapAccountSectionToAuditTag(sectionResponse.columns)];
        let Component;
        if (sectionResponse.data.form) {
            return (React.createElement(AccountSectionForm, { ref: sectionComponentRef, data: props.data, section: sectionResponse, showSubmitButton: props.showSubmitButton, formParams: formParams, onChanged: props.onChanged, onSubmit: (data) => props.onSubmit(data, auditTags), onSubmitSuccess: props.onSubmitSuccess, showInitialErrors: props.showInitialErrors }));
        }
        else if (props.section.columns.key) {
            Component = (_c = (_b = context.accountConfig) === null || _b === void 0 ? void 0 : _b.component) === null || _c === void 0 ? void 0 : _c[props.section.columns.key];
        }
        if (!Component && sectionResponse.data.key === 'membership') {
            if (props.page === 'public-registration') {
                Component = AccountMembershipAnonymous;
            }
            else {
                Component = AccountMembershipUser;
            }
        }
        if (!Component && sectionResponse.data.key === 'groups') {
            Component = AccountGroupUser;
        }
        if (Component) {
            const wizard = (formParams === null || formParams === void 0 ? void 0 : formParams.page) === 'new' || (formParams === null || formParams === void 0 ? void 0 : formParams.page) === 'public-registration' || (formParams === null || formParams === void 0 ? void 0 : formParams.page) === 'login-completion';
            return (React.createElement(Component, { ref: sectionComponentRef, data: props.data, accountId: props.accountId, section: sectionResponse, showSubmitButton: props.showSubmitButton, formParams: formParams, onChanged: (changed, submit) => {
                    if (!wizard) {
                        if (changed) {
                            navigationContext.setChanged(t('common.saveChanges'), submit);
                        }
                        else {
                            navigationContext.clearChanged();
                        }
                    }
                    if (props.onChanged) {
                        props.onChanged(changed, submit);
                    }
                }, onSubmit: (data) => {
                    if (wizard) {
                        const savePromise = props.onSubmit(data, auditTags);
                        navigationContext.save(savePromise);
                        return savePromise;
                    }
                    else {
                        return props.onSubmit(data, auditTags);
                    }
                }, onSubmitSuccess: props.onSubmitSuccess }));
        }
        else {
            return React.createElement(Alert, { color: 'error' }, t('panels.accounts.invalidSectionConfig'));
        }
    }
    else if (sectionError) {
        console.error('Failed to load account section config', sectionError);
        return React.createElement(Alert, { color: 'error' }, t('panels.accounts.failedToLoadSectionConfig'));
    }
    else {
        return null;
    }
});
const AccountSectionForm = React.forwardRef((props, ref) => {
    var _a, _b, _c;
    const { formParams: formConfigFormParams } = React.useContext(FormConfigContext);
    const formRef = React.useRef(null);
    React.useImperativeHandle(ref, () => ({
        getData: () => {
            var _a, _b;
            return (_b = (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.getData()) !== null && _b !== void 0 ? _b : {};
        },
        submit: () => {
            if (formRef.current) {
                return formRef.current.submit();
            }
            return Promise.reject('Something went wrong.');
        },
    }));
    const wizard = ((_a = props.formParams) === null || _a === void 0 ? void 0 : _a.page) === 'new' ||
        ((_b = props.formParams) === null || _b === void 0 ? void 0 : _b.page) === 'public-registration' ||
        ((_c = props.formParams) === null || _c === void 0 ? void 0 : _c.page) === 'login-completion';
    return (React.createElement(Form, { ref: formRef, config: props.section.data.form, data: props.data, formParams: Object.assign(Object.assign({}, formConfigFormParams), props.formParams), hideFormControls: !props.showSubmitButton, trackChanges: wizard ? undefined : props.section.data.autoSaveForm ? 'auto-save' : 'ask-to-save', changeUndefinedTopFieldsToNull: true, onChanged: props.onChanged, onSubmit: props.onSubmit, onSubmitSuccess: props.onSubmitSuccess, showErrors: props.showInitialErrors ? 'all' : 'changed' }));
});
